import { computed, inject, provide, ref, watch } from 'vue';
import { useDocumentEvent } from '../utils/dom';
import { useEvent } from '../plugins/vue-gtag';
import LocalSynthesis from './localSynthesis';
import ServerSynthesis from './serverSynthesis';
import { transformEoToPl } from './esperantoTransformer';
export class TTSManagerService {
  constructor(language, locale, options = {}){
    this.listeners = [];
    this.init(language, locale, options);
  }

  async init(language, locale, { speed, voiceId }){
    window.utterances = []
    this.speed = speed || 3;
    this.language = language;
    this.locale = locale;
    this.index = 0;
    this.playing = false;
    this.texts = [];
    this.voiceId = voiceId;
    const core = await this.getCore();
    return this;
  }

  async getCore(){
    let core;
    if (await ServerSynthesis.isSupported(this.language)){
      core = new ServerSynthesis(this.language, this.voiceId);
    } else if (await LocalSynthesis.isSupported(this.language)){
      core = new LocalSynthesis(this.language, this.locale)
    } else {
      throw new Error('TTS not supported for selected language');
    }
    this.core = core;
    core.setSpeed(this.speed);
    this.notifyListeners('voicesChanged', await core.getVoices());
    this.notifyListeners('voiceChanged', await core.getVoice());
    return core;
  }

  setIpaTexts(ipaTexts){
    this.ipaTexts = ipaTexts;
  }

  addEventListener(eventType, listener){
    if (typeof listener !== 'function'){
      throw new Error('Second parameter must be a function');
    }
    this.listeners[eventType] ||= []; 
    this.listeners[eventType].push(listener);
  }

  removeEventListener(eventType, listener){
    if (typeof listener !== 'function'){
      throw new Error('Second parameter must be a function');
    }
    this.listeners[eventType] ||= []; 
    this.listeners[eventType] = this.listeners[eventType].filter(l => l !== listener);
  }

  notifyListeners(eventType, ...params){
    if (this.listeners[eventType]){
      this.listeners[eventType].forEach(listener => listener(...params))
    }
  }

  setTexts(texts){
    if (this.started){
      this.stop();
    }
    this.texts = texts;
    if (this.core && texts && texts.length > 1){
      this.prepareTexts(this.index, 1);
    }
  }

  async setLanguage(language, locale){
    this.language = language;
    this.locale = locale
    await this.getCore();
  }

  setVoice(voice){
    if (this.core){
      this.core.setVoice(voice);
      this.prepareTexts(this.index, 1);
    }
  }

  setSpeed(speed){
    if (this.core){
      this.core.setSpeed(speed);
      this.prepareTexts(this.index, 1);
    }
  }

  play(){
    if (!this.texts || this.texts.length <= 0) return;
    this.started = true;
    this.resume();
    this.notifyListeners('started');
  }

  pause(){
    this.setPlaying(false);
    this.core.cancel();
  }

  resume(){
    this.setPlaying(true);
    this.speakSynth();
  }

  pauseResume(){
    if(!this.started){
      this.play();
    } else if (this.playing){
      this.pause();
    } else {
      this.resume();
    }
  }

  stop(){
    this.core.cancel();
    this.setPlaying(false);
    this.notifyListeners('ended');
    this.setIndex(0);
    this.started = false;
  }

  setPlaying(val){
    this.playing = val;
    if (val){
      this.notifyListeners('resumed');
    } else {
      this.notifyListeners('paused');
    }
  }

  moveWord(wordIndex){
    if (wordIndex === this.index){
      this.setIndex(this.index + 1);
    }
  }

  setIndex(newIndex){
    this.moveToIndex(newIndex);
    this.notifyListeners('wordChanged', newIndex);
  }

  moveToIndex(newIndex){
    if (this.index == newIndex) return;
    this.index = newIndex;
    if (this.playing){
      this.speakSynth();
    }
  }

  createUtterOptions(index){
    if (this.ipaTexts && this.ipaTexts.length > 0){
      return {
        ipa: this.ipaTexts[index],
        core: this.core
      }
    } else {
      return {
        core: this.core
      }
    }
  }

  playUtters(){
    const currentIndex = this.index; 
    const nextText = this.texts[currentIndex];
    this.prepareTexts(currentIndex + 1);
    this.core.play(nextText, this.createUtterOptions(currentIndex))
    .then(()=>{
      if(!this.playing) return;
      if (currentIndex + 1 >= this.texts.length){
        if (this.index < currentIndex) return; // Allows to move back even if pronouncing last world.
        this.stop();
      } else {
        this.moveWord(currentIndex);
      }
    })
    .catch(err => {
      this.notifyListeners('error', err);
    });
  }

  pronounce(text, options = {}){
    this.core.play(text, options)
    .then(()=>{
      this.notifyListeners('pronounced', text);
    });
  }

  speakSynth(){
    if(this.texts && this.texts.length > 0){
      this.playUtters();
    }
  }

  prepareTexts(start, numberOfTexts = 2){
    if (this.texts && this.core){
      const wordsToPrepare = this.texts.slice(start, start + numberOfTexts)
      const options = wordsToPrepare.map((_, i)=>this.createUtterOptions(i + start))
      this.core.prepareTexts(wordsToPrepare, options);  
    }
  }
}

export function useTTS({
  language = ref('en'), 
  speed = ref(null),
  locale = ref('en-US'),
  playing = ref(false),
  started = ref(false),
  ttsIndex = ref(0),
  textArray = ref([]),
  useIpa = ref(false),
  ipaTexts = ref([]),
  voices = ref([]),
  voice = ref(null),
  voiceId = ref(null),
  ttsMode = ref(false),
}){
  const ttsManagerService = ref(new TTSManagerService(language.value, locale.value, {
    speed: speed.value,
    voiceId: voiceId.value,
  }));

  ttsManagerService.value.addEventListener('wordChanged',(newIndex)=>{
    ttsIndex.value = newIndex;
  })

  ttsManagerService.value.addEventListener('resumed',()=>{
    playing.value = true;
  })

  ttsManagerService.value.addEventListener('paused',()=>{
    playing.value = false;
  })

  ttsManagerService.value.addEventListener('started',()=>{
    started.value = true;
  })

  ttsManagerService.value.addEventListener('ended',()=>{
    ttsManagerService.value.setIndex(0);
    started.value = false;
  })

  ttsManagerService.value.addEventListener('voicesChanged',(newVoices)=>{
    voices.value = newVoices;
  })

  ttsManagerService.value.addEventListener('voiceChanged',(newVoice)=>{
    locale.value = newVoice && newVoice.lang;
    voice.value = newVoice;
  })

  const processedArray = computed(()=>{
    if (language.value === 'eo' && textArray.value){
      return textArray.value.map(transformEoToPl);
    }
    return textArray.value;
  })

  watch(language, (newVal)=>{
    // Trigger watch function for textArray when language changes to esperanto
    ttsManagerService.value.setLanguage(newVal, locale.value);
  })

  watch(processedArray, (newVal)=>{
    ttsManagerService.value.setTexts(newVal);
  })

  watch(voice, (newVoice)=>{
    voiceId.value = newVoice.voiceURI || voiceId.value;
    ttsManagerService.value.setVoice(newVoice)
  })

  watch(speed, (newVal)=>{
    ttsManagerService.value.setSpeed(newVal);
  })

  watch(ttsIndex, (newVal)=>{
    ttsManagerService.value.moveToIndex(newVal);
  })

  watch(ipaTexts, (newVal)=>{
    if (useIpa.value){
      ttsManagerService.value.setIpaTexts(newVal);
    }
  })

  watch(useIpa, (newVal)=>{
    if (!newVal){
      ttsManagerService.value.setIpaTexts(null);
    }
  })

  const handlePlayPause = ()=>{
    ttsManagerService.value.pauseResume();
  }

  const handleVoiceChanged = (newVal)=>{
    locale.value = newVal && newVal.lang;
    localStorage.setItem("last_voice_selection", newVal && newVal.voiceURI);
    localStorage.setItem("last_locale_selection", newVal && newVal.lang);
    voice.value = newVal
  }

  const handleTTSMode = (newVal)=>{
    ttsMode.value = newVal;
  }

  ttsManagerService.value.setTexts(processedArray.value);

  provide('language', language);
  provide('speed', speed);
  provide('ttsIndex', ttsIndex);
  provide('ttsLocale', locale);
  provide('playing', playing);
  provide('started', started);
  provide('textArray', textArray);
  provide('ttsManagerService', ttsManagerService);
  provide('voices', voices);
  provide('voice', voice);
  provide('useIpa', useIpa);
  provide('ipaTexts', ipaTexts);
  provide('ttsMode', ttsMode);
  provide('handlePlayPause', handlePlayPause);
  provide('handleVoiceChanged', handleVoiceChanged);
  provide('handleTTSMode', handleTTSMode);

  return {
    language,
    speed,
    ttsIndex,
    playing,
    started,
    textArray,
    ipaTexts,
    ttsManagerService,
    voices,
    voice,
    handlePlayPause,
    handleVoiceChanged,
    ttsLocale: locale,
    useIpa,
  }
}

export function ttsKeyListeners(disabledKeyControl = ref(false)){
  const ttsManagerService = inject('ttsManagerService', undefined);
  const gaStartTtsKey = useEvent('start_key_tts'); 
  const gaPlayPauseKey = useEvent('play_pause_key_tts'); 

  const handleKeypress = (event)=>{
    if (!disabledKeyControl.value){
      var key = event.key || event.keyCode;
      if (key === ' ' || key === 32){ 
        if (!ttsManagerService.value.started){
          gaStartTtsKey();
        } else {
          gaPlayPauseKey(!ttsManagerService.value.playing ? 'resume' : 'pause');
        }
        ttsManagerService.value.pauseResume();
      }
    }
  }

  useDocumentEvent('keypress', handleKeypress);
}
